<template>
  <div class="impression-capacity">
    <chart
      v-if="data.length > 0"
      ref="impressionsCapacityChart"
      template-ref="impressionsCapacityChart"
      :name="chartName"
      :mode="ChartMode.VERBOSE"
      :actions="chartActions"
      :echarts-options="chartOptions"
      :theme="ChartTheme.TEN_TONES"
      @update-chart-type="updateChartType"
      @timezone-selected="timezoneSelected" />
    <div
      v-else
      class="alert alert-info mb-4">
      {{ $t('messages.noDataAvailable') }}
    </div>
  </div>
</template>

<script setup lang="ts">
  import { DateTime } from 'luxon';
  import { capitalize, computed, onMounted, ref, toRefs } from 'vue';

  import ChartDownloadGroupActionsUtils from '@/common/components/chart/chart-download-group-actions';
  import ChartFullscreenActions from '@/common/components/chart/chart-fullscreen-actions';
  import ChartIntervalGroupActionsUtils from '@/common/components/chart/chart-interval-group-actions';
  import ChartTimezoneGroupActionsUtils from '@/common/components/chart/chart-timezone-group-actions';
  import ChartTrendActionsUtils from '@/common/components/chart/chart-trend-actions';
  import ChartTypeGroupActions from '@/common/components/chart/chart-type-group-actions';
  import CellType from '@/common/enums/cell-type.enum';
  import ChartActionId from '@/common/enums/chart-action-id.enum';
  import ChartMode from '@/common/enums/chart-mode.enum';
  import ChartTheme from '@/common/enums/chart-theme.enum';
  import ChartType from '@/common/enums/chart-type.enum';
  import TimeGranularity from '@/common/enums/time-granularity.enum';
  import { Unit } from '@/common/models';
  import TimeseriesAxisX from '@/common/models/chart/TimeseriesAxisX';
  import LocalCache from '@/utils/LocalCache';
  import formatNumber from '@/utils/NumberFormatter';
  import QuickMathUtils from '@/utils/QuickMathUtils';
  import i18n from '@/vueI18n';

  // Props
  const props = defineProps<{
    flightFrom: DateTime;
    entity: Unit;
    data: number[];
  }>();
  const { flightFrom, entity, data } = toRefs(props);
  const chartType = ref(null);
  const selectedTimezone = ref(null);

  // Computed
  const days = computed(() =>
    data.value.map((_, i) => {
      const date = flightFrom.value.plus({ days: i + 0 });
      return date.toMillis();
    }),
  );

  // Methods
  function impressionSerieFormatter(value: number) {
    return i18n.global.n(value);
  }
  function xAxisTimeFormatter(value: number, zone: string) {
    const date = ChartTimezoneGroupActionsUtils.localizedDate(value, zone);
    return date.toLocaleString(DateTime.DATE_SHORT);
  }
  function tooltipFormatter(params, dataFormatter) {
    const param = params[0];
    let series = `${param.axisValueLabel}<br />`;
    if (param.value !== null && param.value !== undefined) {
      const { marker, seriesName } = param;
      const value = dataFormatter(param.value);
      series += `${marker} ${seriesName}: ${value}<br />`;
    }
    return series;
  }

  // Chart options
  const timeGranularity = TimeGranularity.DAILY;
  const chartName = computed(() => `${capitalize(i18n.global.t('messages.impressionCapacity'))}_${entity.value.name}`);
  const chartGrid = {
    top: 55,
    left: 35,
    right: 10,
    bottom: 40,
  };
  const chartActions = [
    ChartTypeGroupActions.getActions(0, true, [ChartType.LINE, ChartType.BAR], ChartType.BAR),
    ChartTrendActionsUtils.DefaultChartTrendActions,
    ChartTimezoneGroupActionsUtils.DefaultChartTimezoneGroupActions,
    ChartIntervalGroupActionsUtils.ChartIntervalGroupActions(
      2,
      false,
      [timeGranularity],
      timeGranularity,
      QuickMathUtils.sum,
    ),
    ChartDownloadGroupActionsUtils.DefaultChartDownloadOptions,
    ChartFullscreenActions.getActions(1, false),
  ];

  const chartTooltip = {
    trigger: 'axis',
    formatter: (params) => tooltipFormatter(params, (value) => impressionSerieFormatter(value)),
  };

  const chartYAxis = [
    {
      axisLabel: {
        show: true,
        formatter: (params: number) =>
          formatNumber(params, '0a').length === 2 ? formatNumber(params, '0.0a') : formatNumber(params, '0a'),
      },
    },
  ];

  const chartXAxis = computed(() =>
    TimeseriesAxisX.fromObject({
      tz: selectedTimezone.value,
      raw: days.value,
      cellType: CellType.DATE_TIME,
      granularity: timeGranularity,
      transform: xAxisTimeFormatter,
      name: capitalize(i18n.global.t('names.date', { n: 1 })),
      data: days.value.map((t: number) => xAxisTimeFormatter(t, selectedTimezone.value)),
    }),
  );

  const chartData = computed(() => [
    {
      smooth: true,
      data: data.value,
      showAllSymbol: true,
      type: chartType.value,
      cellType: CellType.INTEGER,
      transform: (value: number) => impressionSerieFormatter(value),
      name: capitalize(i18n.global.t('names.impression', { n: 2 })),
    },
  ]);

  const chartOptions = computed(() => ({
    grid: chartGrid,
    calculable: true,
    yAxis: chartYAxis,
    tooltip: chartTooltip,
    xAxis: chartXAxis.value,
    series: chartData.value,
  }));

  // Methods
  function updateChartType(type) {
    chartType.value = type;
  }
  function timezoneSelected(option) {
    selectedTimezone.value = option.label;
  }

  // Lifecycle
  onMounted(() => {
    const settings = LocalCache.get('chartOptions.impressionsCapacityChart');
    if (settings != null) {
      selectedTimezone.value = settings.find((key) => key.id === ChartActionId.TIMEZONE)?.optionSelected;
      chartType.value = settings.find((key) => key.id === ChartActionId.TYPE)?.optionSelected;
    } else {
      const { defaultTz } = ChartTimezoneGroupActionsUtils;
      selectedTimezone.value = defaultTz;
      chartType.value = ChartType.LINE;
    }
  });
</script>

<style lang="scss" scoped>
  .impression-capacity {
    padding: 1.6rem 1.6rem 0;

    .alert {
      margin-top: 0 !important;
    }

    :deep(.chart .body) {
      overflow: hidden;
    }
    :deep(.chart-content) {
      margin-top: -2rem;
    }
  }
</style>
